/**
 * Ember animation clips: what the skeleton does over time.
 *
 * Locomotion is PROCEDURAL. A gait is a handful of numbers (step length, how long each foot stays
 * down, lift, bob, lean, arm swing), each ankle follows a planted-then-swung path and the legs
 * reach it with IK. A planted foot therefore never slides on the art, and the ground a cycle covers
 * is exact: `strideCycle`, which the client turns into cadence. More frames is a parameter, not
 * more drawing.
 *
 * Everything else is KEYFRAMED: a few key poses, eased between, sampled at any frame count.
 */
import { fistCenter, swordAxis, twoHandSpan } from "./body"
import { contentCatalog } from "../content/catalog"
import {
    ArmPose,
    HEEL,
    LegTarget,
    PoseSample,
    Proportions,
    Side,
    Skeleton,
    PELVIS_COUNTER_TWIST,
    WRIST_REST,
    hipOver,
    legAnglesOf,
    legLength,
    maxPelvisHeight,
    restingSample,
    sideSign,
    solvePose,
    standingPelvisHeight,
} from "./rig"
import { IDENTITY, Mat3, Vec3, add, apply, deg, dot, ease, mul, rotX, rotY, rotZ, scale, smoothstep, sub, transpose } from "./vec"

export interface EmberClip {
    name: string
    frames: number
    /** Authored time per frame. Locomotion is re-timed from ground speed instead (see strideCycle). */
    frameMs: number
    loop: boolean
    /** Ground covered by one full cycle, in world pixels. Present on locomotion only. */
    strideCycle?: number
    /** The same, for a gait that scales with the body that walks it (it wins over `strideCycle`). */
    strideFor?: (p: Proportions) => number
    /** `t` is 0..1 across the clip; `facing` for poses drawn differently per view (see restingSample). */
    sample: (t: number, p: Proportions, facing?: string) => PoseSample
}

/** Where frame `i` of `n` samples a clip. A loop never repeats its first frame at the end. */
export const clipTime = (clip: Pick<EmberClip, "loop">, i: number, n: number): number =>
    clip.loop ? i / n : n <= 1 ? 0 : i / (n - 1)

interface Gait {
    /** Distance each ankle travels backwards while planted. */
    stepLength: number
    /** Share of the cycle each foot is on the ground: over 0.5 walks, under 0.5 has a flight phase. */
    duty: number
    lift: number
    /** Extra rise of the heel just after push-off: a runner's heel flicks up behind. */
    heelKick: number
    bob: number
    crouch: number
    lean: number
    armSwing: number
    armForward: number
    elbow: number
    elbowSwing: number
    twist: number
    sway: number
    stanceWidth: number
}

const WALK: Gait = {
    stepLength: 11.5,
    duty: 0.6,
    lift: 2.4,
    heelKick: 0.8,
    bob: 0.7,
    crouch: 0.7,
    lean: deg(3),
    armSwing: deg(28),
    armForward: deg(3),
    elbow: deg(12),
    elbowSwing: deg(16),
    twist: deg(6),
    sway: 0.5,
    stanceWidth: 0.4,
}

const RUN: Gait = {
    stepLength: 14,
    duty: 0.36,
    lift: 3.6,
    heelKick: 5.5,
    bob: 1.3,
    crouch: 1.4,
    lean: deg(12),
    armSwing: deg(46),
    armForward: deg(14),
    elbow: deg(82),
    elbowSwing: deg(14),
    twist: deg(10),
    sway: 0.3,
    stanceWidth: 0.0,
}

/**
 * The lowest the ankle may sit with the foot pitched by `pitch` (toes down positive): whichever of
 * toe or heel the pitch drops is then exactly on the ground. A planted foot sits here; a swinging
 * one never goes below it.
 */
const groundedAnkle = (pitch: number, p: Proportions): number => {
    const sole = p.ankleHeight - p.footRadius
    return p.ankleHeight - sole * (1 - Math.cos(pitch)) + Math.max(p.footLength * Math.sin(pitch), -HEEL * Math.sin(pitch))
}

const footOnPath = (q: number, side: Side, g: Gait, p: Proportions): LegTarget => {
    const x = sideSign(side) * (p.hipWidth + g.stanceWidth)
    const half = g.stepLength / 2
    if (q < g.duty) {
        const s = q / g.duty
        // Heel strike lands toes-up on the heel; the heel peels off the ground before toe-off, the
        // foot pivoting on its toe.
        const strike = -0.25 * (1 - smoothstep(s / 0.25))
        const peel = 0.55 * smoothstep((s - 0.7) / 0.3)
        const pitch = strike + peel
        return { mode: "ik", ankle: [x, groundedAnkle(pitch, p), half - g.stepLength * s], footPitch: pitch, planted: true }
    }
    const s = (q - g.duty) / (1 - g.duty)
    // The foot rises behind first, then swings through: forward travel lags the lift.
    const forward = ease(Math.pow(s, 1.35))
    const kick = g.heelKick * Math.sin(Math.PI * Math.min(1, s * 1.5)) * (1 - s)
    const footPitch = 0.7 * Math.sin(Math.PI * Math.min(1, s * 1.4)) * (1 - s) - 0.25 * smoothstep((s - 0.8) / 0.2)
    const y = Math.max(groundedAnkle(footPitch, p), p.ankleHeight + g.lift * Math.sin(Math.PI * s) + kick)
    return { mode: "ik", ankle: [x, y, -half + g.stepLength * forward], footPitch, planted: false }
}

const sampleGait = (g: Gait) => (t: number, p: Proportions): PoseSample => {
    const L = footOnPath(t % 1, "L", g, p)
    const R = footOnPath((t + 0.5) % 1, "R", g, p)

    // A walk is highest over the planted foot; a run is lowest there and rises through the flight.
    const wave = Math.cos(4 * Math.PI * (t - g.duty / 2))
    let height = standingPelvisHeight(p) - g.crouch + (g.duty >= 0.5 ? g.bob : -g.bob) * wave
    const twist = -g.twist * Math.cos(2 * Math.PI * t)
    const sway = g.sway * Math.cos(2 * Math.PI * (t - g.duty / 2))
    // Never stand so tall that a planted foot is torn off the ground.
    if (L.planted) height = Math.min(height, maxPelvisHeight(L.ankle, hipOver("L", sway, twist, p), p))
    if (R.planted) height = Math.min(height, maxPelvisHeight(R.ankle, hipOver("R", sway, twist, p), p))

    // Each arm swings against its own side's leg.
    const swing = Math.cos(2 * Math.PI * t)
    const armL = g.armForward - g.armSwing * swing
    const armR = g.armForward + g.armSwing * swing
    const elbow = (pitch: number) => g.elbow + g.elbowSwing * Math.max(0, pitch / Math.max(1e-6, g.armSwing))

    return {
        root: [sway, height, 0],
        rootRotation: IDENTITY,
        lean: g.lean,
        // The chest turns with the forward arm: left arm forward turns the left shoulder forward.
        twist,
        sideBend: 0,
        headPitch: -g.lean * 0.4,
        headYaw: 0,
        arms: {
            L: { pitch: armL, abduct: deg(5), flex: elbow(armL) },
            R: { pitch: armR, abduct: deg(5), flex: elbow(armR) },
        },
        legs: { L, R },
    }
}

/** Ground one full cycle covers: during stance the body passes over the planted foot. */
const strideOf = (g: Gait) => g.stepLength / g.duty

// ---- natural gait -------------------------------------------------------------------------

/**
 * A gait for a body at real human proportions, drawn from how people actually move rather than
 * from LPC's few frames. The swing leg follows the hip and knee angles of recorded human gait
 * (Winter's walking data, Novacheck's running), so the heel kicks up behind and the knee drives
 * through instead of the leg swinging stiff; the stance foot strikes heel first and rolls off its
 * toes; the pelvis dips when both feet are down, hips and shoulders counter-rotate, the arms swing
 * with their elbows giving more on the way forward, and the head stays level. Lengths are shares
 * of the leg, so any body walks it at its own scale.
 */
interface Stride {
    /** Ground one full cycle covers, in leg lengths. */
    cycle: number
    /** Share of the cycle each foot is down: over 0.5 walks, under 0.5 runs with a flight phase. */
    duty: number
    /**
     * Share of a foot's travel under the body that is ahead of the hip when it lands. People land
     * closer under themselves than they push off behind, where the foot rolling onto its toes
     * lengthens the leg -- an even split drops the hips hard at every heel strike.
     */
    reach: number
    /** Feet placed this far outside (+) or inside (-) the hip joints, in leg lengths. */
    stanceWidth: number
    /** How much lower than standing the pelvis travels, and how far it rises and falls, in leg lengths. */
    crouch: number
    bob: number
    lean: number
    twist: number
    /** Side-to-side shift over the standing foot, in leg lengths. */
    sway: number
    /** Foot pitch (toes down +) as it lands; share of stance at which it is flat, at which the heel lifts; pitch at toe-off. */
    strike: number
    flatBy: number
    heelOff: number
    toeOff: number
    /** Over the swing (0 toe-off .. 1 contact), in degrees: hip flexion, knee flexion, ankle plantarflexion. */
    hip: [number, number][]
    knee: [number, number][]
    ankle: [number, number][]
    arm: { swing: number; forward: number; abduct: number; elbow: number; elbowForward: number; lag: number }
}

/** A curve through [s, degrees] knots, eased between them; radians out. */
const curveAt = (knots: [number, number][], s: number): number => {
    let i = 0
    while (i < knots.length - 2 && s > knots[i + 1][0]) i++
    const [s0, v0] = knots[i], [s1, v1] = knots[i + 1]
    return deg(v0 + (v1 - v0) * ease(s1 > s0 ? (s - s0) / (s1 - s0) : 0))
}

const NATURAL_WALK: Stride = {
    cycle: 1.7,
    duty: 0.6,
    reach: 0.42,
    stanceWidth: -0.01,
    crouch: 0.06,
    bob: 0.035,
    lean: deg(3),
    twist: deg(5),
    sway: 0.02,
    strike: -0.32,
    flatBy: 0.15,
    heelOff: 0.62,
    toeOff: 0.9,
    hip: [[0, -8], [0.2, 8], [0.5, 26], [0.75, 31], [1, 28]],
    knee: [[0, 40], [0.25, 62], [0.5, 40], [0.75, 12], [0.92, 3], [1, 5]],
    ankle: [[0, 15], [0.3, 3], [0.6, 0], [1, 0]],
    arm: { swing: deg(20), forward: deg(2), abduct: deg(8), elbow: deg(15), elbowForward: deg(25), lag: 0.03 },
}

const NATURAL_RUN: Stride = {
    cycle: 2.8,
    duty: 0.33,
    reach: 0.35,
    stanceWidth: -0.02,
    crouch: 0.04,
    bob: 0.04,
    lean: deg(9),
    twist: deg(10),
    sway: 0.01,
    strike: -0.15,
    flatBy: 0.15,
    heelOff: 0.5,
    toeOff: 0.7,
    hip: [[0, -10], [0.25, 8], [0.55, 40], [0.8, 42], [1, 30]],
    knee: [[0, 22], [0.3, 80], [0.55, 74], [0.8, 36], [1, 22]],
    ankle: [[0, 25], [0.35, 8], [1, 0]],
    // A runner's elbow opens as the arm drives back and closes as it comes forward.
    arm: { swing: deg(30), forward: deg(4), abduct: deg(12), elbow: deg(64), elbowForward: deg(46), lag: 0.02 },
}

const sampleStride = (g: Stride) => (t: number, p: Proportions): PoseSample => {
    const leg = legLength(p)
    const travel = g.cycle * leg * g.duty
    const ahead = travel * g.reach
    const x = (side: Side) => sideSign(side) * (p.hipWidth + g.stanceWidth * leg)
    // A walk rides highest over the standing foot and dips as both feet touch; a run sinks onto
    // the standing foot and rises through the flight. Twice a cycle either way.
    const heightAt = (q: number) =>
        standingPelvisHeight(p) - g.crouch * leg + (g.duty >= 0.5 ? 1 : -1) * g.bob * leg * Math.cos(4 * Math.PI * (q - g.duty / 2))
    const stancePitch = (s: number) =>
        g.strike * (1 - smoothstep(s / g.flatBy)) + g.toeOff * smoothstep((s - g.heelOff) / (1 - g.heelOff))
    // Where the hip and knee angles of swing put the ankle, relative to the pelvis, and the shank's tilt.
    const swingLeg = (s: number): [number, number, number] => {
        const hipY = heightAt(g.duty + s * (1 - g.duty)) - p.hipDrop
        const thigh = curveAt(g.hip, s)
        const shank = thigh - curveAt(g.knee, s)
        return [
            p.thigh * Math.sin(thigh) + p.shin * Math.sin(shank),
            hipY - p.thigh * Math.cos(thigh) - p.shin * Math.cos(shank),
            shank,
        ]
    }
    const [z0, y0, shank0] = swingLeg(0)
    const [z1, y1, shank1] = swingLeg(1)
    const lift = [ahead - travel - z0, groundedAnkle(g.toeOff, p) - y0]
    const land = [ahead - z1, groundedAnkle(g.strike, p) - y1]
    const free = (s: number, shank: number) => -shank + curveAt(g.ankle, s)

    const foot = (q: number, side: Side): LegTarget => {
        if (q < g.duty) {
            const s = q / g.duty
            const pitch = stancePitch(s)
            return { mode: "ik", ankle: [x(side), groundedAnkle(pitch, p), ahead - travel * s], footPitch: pitch, planted: true }
        }
        const s = (q - g.duty) / (1 - g.duty)
        const [z, y, shank] = swingLeg(s)
        // The recorded path, bent just enough to leave the toe-off pose and land on the strike pose.
        const footPitch =
            free(s, shank) +
            (1 - smoothstep(s / 0.2)) * (g.toeOff - free(0, shank0)) +
            smoothstep((s - 0.8) / 0.2) * (g.strike - free(1, shank1))
        const ankleY = y + (1 - s) * lift[1] + s * land[1]
        return {
            mode: "ik",
            ankle: [x(side), Math.max(groundedAnkle(footPitch, p), ankleY), z + (1 - s) * lift[0] + s * land[0]],
            footPitch,
            planted: false,
        }
    }

    const L = foot(t % 1, "L")
    const R = foot((t + 0.5) % 1, "R")
    let height = heightAt(t)
    // The chest turns with the forward arm, the pelvis against it (solvePose), the weight over the standing foot.
    const twist = -g.twist * Math.cos(2 * Math.PI * t)
    const sway = g.sway * leg * Math.cos(2 * Math.PI * (t - g.duty / 2))
    if (L.planted) height = Math.min(height, maxPelvisHeight(L.ankle, hipOver("L", sway, twist, p), p))
    if (R.planted) height = Math.min(height, maxPelvisHeight(R.ankle, hipOver("R", sway, twist, p), p))

    // Each arm swings against its own side's leg, a beat behind it, the elbow bending more forward.
    const a = g.arm
    const swing = Math.cos(2 * Math.PI * (t - a.lag))
    const arm = (pitch: number) => ({
        pitch,
        abduct: a.abduct,
        flex: a.elbow + a.elbowForward * Math.max(0, (pitch - a.forward) / a.swing),
    })
    return {
        root: [sway, height, 0],
        rootRotation: IDENTITY,
        lean: g.lean,
        twist,
        sideBend: 0,
        // The head rides with the spine, leaning with it (solvePose gives it 0.35 of the lean;
        // this brings it to 0.6), eyes ahead; it does undo the chest's turn.
        headPitch: g.lean * 0.25,
        headYaw: -twist * 0.4,
        arms: { L: arm(a.forward - a.swing * swing), R: arm(a.forward + a.swing * swing) },
        legs: { L, R },
    }
}

// ---- keyframed clips -----------------------------------------------------------------------

/** One key pose. Arms are [pitch, abduct, flex, wrist?]; legs [pitch, abduct, flex, turn?, point?]. */
interface Key {
    t: number
    rise?: number
    shiftX?: number
    roll?: number
    /** The whole body pitched forward about the pelvis, toward the way it faces (a fall onto its face). */
    pitch?: number
    /**
     * How far the body is let down onto whatever of it is lowest -- soles, knees, hands, face --
     * instead of held at `rise`: 1 rests it on the ground, whatever its build or pose.
     */
    ground?: number
    lean?: number
    twist?: number
    /**
     * The pelvis's own turn, and the head's, both relative to the feet. Given, `twist` is the chest's
     * turn too and the body is rotated to match; otherwise the pelvis counter-turns against the
     * chest as it does when walking (solvePose).
     */
    hips?: number
    head?: number
    sideBend?: number
    headPitch?: number
    /** [pitch, abduct, flex] and optionally the wrist's cock (rig ArmPose.wrist), for a held tool. */
    armL: ArmKey
    armR: ArmKey
    /** Planted ankles [x, z] for clips that keep their feet down (and lifted off the ground by a third), else leg angles. */
    feetL?: FeetKey
    feetR?: FeetKey
    /** Planted feet's knees pointed out from ahead by this much: a squat with the knees spread. */
    kneesOut?: number
    /**
     * Leg angles [pitch, abduct, flex], then the hip's turn about the thigh (the knee out: a shin
     * folded across in front, rig LegAngles.turn) and how far the foot points (toes down from
     * square to the shin) -- given a turn, the foot hangs off the shin.
     */
    legL?: LegKey
    legR?: LegKey
    /**
     * The body's own standing pose in the facing drawn (rig restingSample, empty hands open): its
     * idle exactly, whatever its build, so a clip that starts and ends on one sets out from rest and
     * comes back to it without a jump. The key's other poses are not read.
     */
    rest?: boolean
    /** The motion carries on through this key instead of easing to a stop at it. */
    through?: boolean
    /** Both hands open, as at rest, rather than closed. */
    open?: boolean
}

type ArmKey = [number, number, number] | [number, number, number, number]
type LegKey = [number, number, number] | [number, number, number, number] | [number, number, number, number, number]
type FeetKey = [number, number] | [number, number, number]
type Channel = (of: (k: Key, at: number) => number | undefined) => number

const mix = (a: number | undefined, b: number | undefined, u: number) => (a ?? 0) + ((b ?? 0) - (a ?? 0)) * u

/** An unturned leg posed by angles keeps its foot toes-down this far (rig LegAngles.footPitch). */
const FK_FOOT_PITCH = 0.2

/** A key as one body in one facing poses it: a rest key filled in from the body's standing pose. */
const posed = (k: Key, p: Proportions, facing?: string): Key => {
    if (!k.rest) return k
    const rest = restingSample(p, facing)
    const feet = (side: Side): [number, number] => {
        const ankle = (rest.legs[side] as LegTarget).ankle
        return [Math.abs(ankle[0]) - p.hipWidth, ankle[2]]
    }
    const arm = (side: Side): ArmKey => [rest.arms[side].pitch, rest.arms[side].abduct, rest.arms[side].flex]
    return {
        t: k.t,
        through: k.through,
        rise: rest.root[1] - (standingPelvisHeight(p) - 0.4),
        lean: rest.lean,
        headPitch: rest.headPitch,
        armL: arm("L"),
        armR: arm("R"),
        feetL: feet("L"),
        feetR: feet("R"),
        open: true,
    }
}

/**
 * Sample keys over a clip. Between two keys the pose eases out of the one and into the next; a
 * `through` key is passed through instead, at the speed its neighbours set (a Catmull-Rom tangent),
 * so a move made of several keys flows rather than stopping at each. A leg planted by its ankle
 * (feet) and keyed next by its angles (legs) eases from the angles that pose it as planted.
 */
const sampleKeys = (source: Key[]) => (t: number, p: Proportions, facing?: string): PoseSample => {
    const keys = source.map(k => posed(k, p, facing))
    let i = 0
    while (i < keys.length - 2 && t > keys[i + 1].t) i++
    const a = keys[i], b = keys[i + 1]
    const s = b.t > a.t ? (t - a.t) / (b.t - a.t) : 0
    const u = ease(s)
    const flowing = !!(a.through || b.through)
    // One channel of the pose between a and b, read off each key (and, flowing, its neighbours).
    const between: Channel = of => {
        if (!flowing) return mix(of(a, i), of(b, i + 1), u)
        const value = (at: number) => of(keys[at], at) ?? 0
        const tangent = (at: number) =>
            !keys[at].through || at === 0 || at === keys.length - 1 ? 0 : (value(at + 1) - value(at - 1)) / (keys[at + 1].t - keys[at - 1].t)
        const dt = b.t - a.t, s2 = s * s, s3 = s2 * s
        return (2 * s3 - 3 * s2 + 1) * value(i) + (s3 - 2 * s2 + s) * dt * tangent(i) + (-2 * s3 + 3 * s2) * value(i + 1) + (s3 - s2) * dt * tangent(i + 1)
    }
    const rest = restingSample(p)
    const standing = (rise: number) => standingPelvisHeight(p) - 0.4 + rise

    // A key's leg as angles: [pitch, abduct, flex, turn, point, how far the foot follows the shin,
    // its pitch while it keeps the pelvis's heading]. Eased into a turned leg, a planted one or one
    // posed by unturned angles is the turned leg that puts its knee and ankle where the key alone
    // has them, its foot still keeping its heading (so it turns with the leg only as they ease).
    const solved = new Map<string, number[]>()
    const anglesOf = (k: Key, at: number, side: Side, turned: boolean): number[] => {
        const feet = side === "L" ? k.feetL : k.feetR
        const legs = (side === "L" ? k.legL : k.legR) ?? [0, 0, 0]
        if (!feet && (!turned || legs.length >= 4)) return [legs[0], legs[1], legs[2], legs[3] ?? 0, legs[4] ?? 0, 1, 0]
        const id = `${at}${side}`
        if (!solved.has(id)) {
            const leg = legAnglesOf(solvePose(alone(k, at), p), side)
            solved.set(id, [leg.pitch, leg.abduct, leg.flex, leg.turn, leg.footPitch, 0, feet ? 0 : FK_FOOT_PITCH])
        }
        return solved.get(id)!
    }
    const leg = (side: Side, ka: Key, kb: Key, local: Channel): PoseSample["legs"]["L"] => {
        const feetOf = (k: Key) => (side === "L" ? k.feetL : k.feetR)
        const fa = feetOf(ka), fb = feetOf(kb)
        if (fa && fb) {
            const [x, z, lift] = [0, 1, 2].map(n => local(k => (feetOf(k) ?? fa)[n]))
            const kneeOut = local(k => k.kneesOut)
            return { mode: "ik", ankle: [sideSign(side) * (p.hipWidth + x), p.ankleHeight + lift, z], footPitch: 0, ...(kneeOut ? { kneeOut } : {}) }
        }
        const legsOf = (k: Key) => (side === "L" ? k.legL : k.legR)
        const turned = !!fa || !!fb || (legsOf(ka)?.length ?? 3) >= 4 || (legsOf(kb)?.length ?? 3) >= 4
        const [pitch, abduct, flex, turn, point, footFollow, footPitchAhead] = [0, 1, 2, 3, 4, 5, 6].map(n => local((k, at) => anglesOf(k, at, side, turned)[n]))
        if (!turned) return { mode: "fk", pitch, abduct, flex, footPitch: FK_FOOT_PITCH }
        return { mode: "fk", pitch, abduct, flex, turn, footPitch: point, ...(footFollow < 1 ? { footFollow: Math.max(0, footFollow), footPitchAhead } : {}) }
    }
    // The pose between keys ka and kb, each channel read by `local`, the hands as the nearer key has them.
    const pose = (ka: Key, kb: Key, local: Channel, nearB: boolean): PoseSample => {
        const value = (name: keyof Key) => local(k => k[name] as number | undefined)
        const arm = (side: Side) => {
            const of = (k: Key) => (side === "L" ? k.armL : k.armR) ?? [0, 0, 0]
            const [pitch, abduct, flex] = [0, 1, 2].map(n => local(k => of(k)[n]))
            const open = (nearB ? kb : ka).open
            return { pitch, abduct, flex, wrist: local(k => of(k)[3] ?? WRIST_REST), ...(open ? { open } : {}) }
        }
        const roll = value("roll")
        let twist = value("twist")
        const pitch = value("pitch")
        let rootRotation = mul(pitch ? rotX(pitch) : IDENTITY, roll ? rotZ(roll) : IDENTITY)
        let headYaw = rest.headYaw
        if (ka.hips !== undefined && kb.hips !== undefined) {
            // Chest at `twist`, pelvis at `hips`: solvePose turns the chest by root + twist and the
            // pelvis by root - PELVIS_COUNTER_TWIST * twist.
            const chest = twist, hips = value("hips")
            twist = (chest - hips) / (1 + PELVIS_COUNTER_TWIST)
            const yaw = hips + PELVIS_COUNTER_TWIST * twist
            rootRotation = mul(rotY(yaw), rootRotation)
            // The head turns by root + 0.4 twist + headYaw.
            headYaw = value("head") - yaw - 0.4 * twist
        }
        const height = standing(value("rise"))
        const sample: PoseSample = {
            ...rest,
            root: [value("shiftX"), height, 0],
            rootRotation,
            lean: value("lean"),
            twist,
            headYaw,
            sideBend: value("sideBend"),
            headPitch: value("headPitch"),
            arms: { L: arm("L"), R: arm("R") },
            legs: { L: leg("L", ka, kb, local), R: leg("R", ka, kb, local) },
        }
        const ground = Math.max(0, Math.min(1, value("ground")))
        if (ground) sample.root = [sample.root[0], height - ground * lowestPoint(solvePose(sample, p), p), 0]
        return sample
    }
    // A key's pose on its own, as the clip shows it at that key's time.
    function alone(k: Key, at: number): PoseSample {
        return pose(k, k, of => of(k, at) ?? 0, false)
    }
    return pose(a, b, between, u >= 0.5)
}

/** How far below its centre an ellipsoid of these radii, turned by `basis`, reaches. */
const depthBelow = (basis: Mat3, r: Vec3) => Math.hypot(basis[3] * r[0], basis[4] * r[1], basis[5] * r[2])

/** What stands off the skull (hair, nose, jaw) and how far a fist reaches past its round, in pixels. */
const HEAD_REACH = 1.5
const FIST_REACH = 1

/** The height of the lowest of a posed body's surface: what rests on the ground when it is let down. */
const lowestPoint = (sk: Skeleton, p: Proportions): number =>
    Math.min(
        sk.head[1] - depthBelow(sk.headBasis, p.head) - HEAD_REACH,
        sk.chest[1] - depthBelow(sk.torsoBasis, p.torso),
        sk.pelvis[1] - depthBelow(sk.pelvisBasis, p.pelvis),
        ...[sk.L, sk.R].flatMap(limb => [
            limb.elbow[1] - p.forearmRadius,
            limb.hand[1] - p.handRadius - FIST_REACH,
            limb.knee[1] - p.thighRadius,
            limb.heel[1] - p.footRadius,
            limb.toe[1] - p.footRadius,
        ]),
    )

const READY_SLASH: Key = {
    t: 0,
    armL: [deg(10), deg(18), deg(30)],
    armR: [deg(25), deg(14), deg(55)],
    feetL: [0.6, 2.6],
    feetR: [0.6, -2.4],
}

/** A right-handed overhead-to-across strike. Timed like the LPC slash it stands in for (~400ms). */
const SLASH_KEYS: Key[] = [
    READY_SLASH,
    {
        t: 0.3,
        rise: -0.4,
        lean: deg(-4),
        twist: deg(-28),
        armL: [deg(25), deg(22), deg(40)],
        armR: [deg(150), deg(30), deg(70), deg(90)],
        feetL: [0.6, 2.6],
        feetR: [0.6, -2.4],
    },
    {
        t: 0.55,
        rise: -1.4,
        lean: deg(12),
        twist: deg(30),
        armL: [deg(-20), deg(24), deg(35)],
        armR: [deg(75), deg(-25), deg(8), deg(20)],
        feetL: [0.6, 3.2],
        feetR: [0.6, -2.8],
    },
    {
        t: 0.75,
        rise: -1.2,
        lean: deg(14),
        twist: deg(34),
        armL: [deg(-15), deg(22), deg(35)],
        armR: [deg(15), deg(-40), deg(15), deg(30)],
        feetL: [0.6, 3.2],
        feetR: [0.6, -2.8],
    },
    { ...READY_SLASH, t: 1 },
]

/**
 * LPC's `hurt` row: the knees go, the body folds down over them, pitches forward onto its hands and
 * ends flat on its face toward the camera -- the crown of the head nearest, the back and legs
 * trailing away behind, soles up -- and stays there.
 *
 * Lying truly flat it would be a sliver at this camera (12 degrees down): the fallen body lies on
 * the slope LPC's art implies instead, head down, so its length climbs the frame as depth does.
 * Every key after the first rests the body on whatever of it is lowest (Key.ground): feet, then
 * knees and hands, then face and chest.
 */
const FALL_KEYS: Key[] = [
    {
        t: 0,
        armL: [deg(5), deg(12), deg(10)],
        armR: [deg(5), deg(12), deg(10)],
        legL: [0, deg(3), 0],
        legR: [0, deg(3), 0],
    },
    // The knees buckle; the head drops.
    {
        t: 0.25,
        ground: 1,
        lean: deg(12),
        headPitch: deg(16),
        armL: [deg(12), deg(24), deg(25)],
        armR: [deg(12), deg(24), deg(25)],
        legL: [deg(40), deg(8), deg(70)],
        legR: [deg(40), deg(8), deg(70)],
    },
    // Sunk to a crouch over the knees, hands going down to catch it.
    {
        t: 0.5,
        ground: 1,
        lean: deg(32),
        headPitch: deg(6),
        armL: [deg(40), deg(28), deg(30)],
        armR: [deg(40), deg(28), deg(30)],
        legL: [deg(85), deg(10), deg(125)],
        legR: [deg(85), deg(10), deg(125)],
    },
    // Pitched forward onto the hands: the crown to the camera.
    {
        t: 0.75,
        ground: 1,
        pitch: deg(55),
        lean: deg(18),
        headPitch: deg(22),
        armL: [deg(75), deg(40), deg(35)],
        armR: [deg(75), deg(40), deg(35)],
        legL: [deg(55), deg(12), deg(100)],
        legR: [deg(55), deg(12), deg(100)],
    },
    // Face down, elbows out and hands by the head, legs splayed behind with the soles up.
    {
        t: 1,
        ground: 1,
        pitch: deg(100),
        headPitch: deg(8),
        armL: [deg(45), deg(70), deg(95)],
        armR: [deg(45), deg(70), deg(95)],
        legL: [deg(-6), deg(18), deg(6)],
        legR: [deg(-6), deg(18), deg(6)],
    },
]

/**
 * A dagger's slash, from any ready stance: the knife drawn back by the right shoulder, then cut
 * across the body at chest height, edge leading, and recovered -- quicker and closer than the axe's
 * overhead chop, which a short blade has no weight for.
 */
const daggerSlash = (ready: Key): Key[] => {
    const guard: Key = { ...ready, armR: [deg(30), deg(15), deg(70), deg(80)], armL: [deg(25), deg(15), deg(60)] }
    return [
        guard,
        { ...guard, t: 0.3, rise: -0.3, lean: deg(-2), twist: deg(-22), hips: ready.hips !== undefined ? deg(-6) : undefined,
          armR: [deg(60), deg(55), deg(100), deg(70)], armL: [deg(30), deg(18), deg(55)] },
        { ...guard, t: 0.55, rise: -0.8, lean: deg(6), twist: deg(24), hips: ready.hips !== undefined ? deg(7) : undefined,
          armR: [deg(72), deg(-35), deg(22), deg(15)], armL: [deg(15), deg(20), deg(50)] },
        { ...guard, t: 0.75, rise: -0.7, lean: deg(7), twist: deg(30), hips: ready.hips !== undefined ? deg(8) : undefined,
          armR: [deg(48), deg(-55), deg(38), deg(25)], armL: [deg(12), deg(20), deg(50)] },
        { ...guard, t: 1 },
    ]
}

/**
 * A longsword's cut, two-handed: from the guard at the right shoulder with the blade upright, the
 * sword swung back over the shoulder, then cut down and across with both arms driving it and the
 * body turning in behind them, finishing low on the left, and recovered. The left hand's place is
 * on the grip (twoHanded), so only the right arm is keyed.
 */
const longswordCut = (ready: Key): Key[] => {
    const turn = (a: number) => (ready.hips !== undefined ? deg(a) : undefined)
    const guard: Key = { ...ready, armR: [deg(60), deg(25), deg(100), deg(20)] }
    return [
        guard,
        { ...guard, t: 0.3, rise: -0.5, lean: deg(-4), twist: deg(-35), hips: turn(-10), head: turn(-6),
          armR: [deg(130), deg(30), deg(90), deg(40)] },
        { ...guard, t: 0.55, rise: -1.6, lean: deg(10), twist: deg(28), hips: turn(10), head: turn(6),
          armR: [deg(85), deg(-15), deg(15), deg(0)] },
        { ...guard, t: 0.75, rise: -1.4, lean: deg(12), twist: deg(32), hips: turn(12), head: turn(6),
          armR: [deg(35), deg(-30), deg(25), deg(10)] },
        { ...guard, t: 1 },
    ]
}

/**
 * A saber's cut, one-handed: from a guard with the point up, the blade swung back over the right
 * shoulder, cut down and across the body with the arm straightening behind it, and recovered. The
 * free hand rests on the hip, out of the blade's way (handOnHip).
 */
const saberCut = (ready: Key): Key[] => {
    const turn = (a: number) => (ready.hips !== undefined ? deg(a) : undefined)
    const guard: Key = { ...ready, armR: [deg(30), deg(14), deg(60), deg(45)] }
    return [
        guard,
        { ...guard, t: 0.3, rise: -0.3, lean: deg(-3), twist: deg(-26), hips: turn(-6), head: turn(-5),
          armR: [deg(160), deg(35), deg(50), deg(90)] },
        { ...guard, t: 0.55, rise: -0.9, lean: deg(7), twist: deg(24), hips: turn(7), head: turn(5),
          armR: [deg(80), deg(-20), deg(10), deg(10)] },
        { ...guard, t: 0.75, rise: -0.8, lean: deg(9), twist: deg(30), hips: turn(8), head: turn(5),
          armR: [deg(30), deg(-45), deg(15), deg(20)] },
        { ...guard, t: 1 },
    ]
}

type Sampler = EmberClip["sample"]

/**
 * Put one hand's fist at `fist` (world), the elbow bending toward `pole` (chest frame): the arm
 * reaches for the wrist that leaves the fist there (rig ArmPose.reach), corrected a few times for
 * the fist sitting past the wrist.
 */
const placeHand = (pose: PoseSample, p: Proportions, side: Side, fist: Vec3, pole: Vec3): PoseSample => {
    const sk = solvePose(pose, p)
    const toChest = transpose(sk.torsoBasis)
    const reaching = (wrist: Vec3): PoseSample => {
        const arm: ArmPose = { ...pose.arms[side], reach: apply(toChest, sub(wrist, sk[side].shoulder)), pole }
        return { ...pose, arms: { ...pose.arms, [side]: arm } }
    }
    let wrist = fist
    for (let i = 0; i < 3; i++) wrist = add(wrist, sub(fist, fistCenter(solvePose(reaching(wrist), p), p, side)))
    return reaching(wrist)
}

/** Both hands on the hilt: the left fist on the grip just behind the right, wherever the right arm is. */
const twoHanded = (sample: Sampler, span: (p: Proportions) => number = twoHandSpan): Sampler => (t, p, facing) => {
    const pose = sample(t, p, facing)
    const sk = solvePose(pose, p)
    const fist = sub(fistCenter(sk, p, "R"), scale(swordAxis(sk.R), span(p)))
    return placeHand(pose, p, "L", fist, [0.7, -1, -0.2])
}

/** How far apart the hands hold a pole or a great axe's haft: wider than a sword's grip. */
const poleSpan = (p: Proportions) => p.handRadius * 3.2

/**
 * Where a cut facing the camera puts the sword hand: the fist's offset from the chest and the
 * blade's direction, in the body's frame (+x its left, +z ahead), the offset in arm lengths.
 */
interface CutPlace { t: number; fist: Vec3; blade: Vec3 }

/**
 * Facing the camera: from the guard, back over the right shoulder, over the head, then down in
 * front of the body to the left, blade forward and down at the hit, and low on the left after.
 */
const LONGSWORD_TO_CAMERA: CutPlace[] = [
    { t: 0, fist: [0.03, 0.28, 0.54], blade: [0.05, 1, 0] },
    { t: 0.3, fist: [-0.3, 0.55, 0.05], blade: [-0.45, 0.55, -0.7] },
    { t: 0.43, fist: [-0.12, 0.5, 0.4], blade: [-0.1, 0.95, 0.3] },
    { t: 0.55, fist: [0.08, -0.05, 0.65], blade: [0.3, -0.65, 0.7] },
    { t: 0.75, fist: [0.28, -0.45, 0.3], blade: [0.45, -0.85, 0.25] },
    { t: 1, fist: [0.03, 0.28, 0.54], blade: [0.05, 1, 0] },
]
/** The saber's, one-handed: the guard out ahead, point up, and a longer reach at the hit. */
const SABER_TO_CAMERA: CutPlace[] = [
    { t: 0, fist: [-0.12, 0.05, 0.55], blade: [0.05, 0.75, 0.65] },
    { t: 0.3, fist: [-0.32, 0.55, 0.05], blade: [-0.4, 0.5, -0.75] },
    { t: 0.43, fist: [-0.15, 0.45, 0.42], blade: [-0.15, 0.95, 0.25] },
    { t: 0.55, fist: [0.02, -0.05, 0.68], blade: [0.3, -0.6, 0.72] },
    { t: 0.75, fist: [0.26, -0.42, 0.32], blade: [0.5, -0.8, 0.3] },
    { t: 1, fist: [-0.12, 0.05, 0.55], blade: [0.05, 0.75, 0.65] },
]

/**
 * A sword's cut by facing. Side-on it lands level, straight ahead, across the view: keyed angles.
 * Facing the camera (or away from it) a level cut points straight at the viewer and vanishes on
 * the very frame it lands, and one swept sideways reads as striking at something beside him; there
 * it comes DOWN in front of the body on a diagonal from over the right shoulder, as LPC's
 * front-facing slash does. The body turns as side-on; the hand is placed (`toCamera`), since an
 * arm raised past level abducts toward the midline and keyed angles fought the shape.
 */
const swordCut = (keys: Key[], toCamera: CutPlace[]): Sampler => {
    const level = sampleKeys(keys)
    return (t, p, facing) => {
        const pose = level(t, p)
        if (facing !== "front" && facing !== "back") return pose
        let i = 0
        while (i < toCamera.length - 2 && t > toCamera[i + 1].t) i++
        const a = toCamera[i], b = toCamera[i + 1]
        const u = ease(b.t > a.t ? (t - a.t) / (b.t - a.t) : 0)
        const sk = solvePose(pose, p)
        const fist = add(sk.chest, scale(lerp3(a.fist, b.fist, u), p.upperArm + p.forearm))
        const grip = apply(transpose(sk.torsoBasis), unit(lerp3(unit(a.blade), unit(b.blade), u)))
        const holding = { ...pose, arms: { ...pose.arms, R: { ...pose.arms.R, grip } } }
        return placeHand(holding, p, "R", fist, [-1, -0.5, -0.2])
    }
}

/** The left fist on the hip's crest, the elbow out to the side and back. */
const handOnHip = (sample: Sampler): Sampler => (t, p, facing) => {
    const pose = sample(t, p, facing)
    const sk = solvePose(pose, p)
    const crest = add(sk.pelvis, apply(sk.pelvisBasis, [p.pelvis[0] + p.handRadius * 0.6, p.pelvis[1] * 0.3, -0.5]))
    return placeHand(pose, p, "L", crest, [1, 0.1, -0.7])
}

/**
 * A bow shot, as an archer looses one: turning side-on to the target, raising the bow and nocking,
 * drawing to an anchor under the jaw with the bow arm out level with it, a beat at full draw, then
 * the loose -- just past halfway, where the game fires the arrow -- the drawing hand flying back
 * past the ear, and the recovery. The hands are placed, not posed (rig ArmPose.reach); `stance` is
 * how far the feet step apart along the line of the shot, in leg lengths.
 */
type BowAt = "rest" | "raise" | "aim" | "follow"
type HandAt = "rest" | "nock" | "anchor" | "loose"
interface ShotKey { t: number; chest: number; hips: number; lean: number; rise: number; bow: BowAt; hand: HandAt; draw: number; feet: number }
const SHOT_KEYS: ShotKey[] = [
    { t: 0, chest: 0, hips: 0, lean: 0, rise: 0, bow: "rest", hand: "rest", draw: 0, feet: 0 },
    { t: 0.18, chest: deg(-55), hips: deg(-35), lean: deg(2), rise: -0.4, bow: "raise", hand: "nock", draw: 0.05, feet: 1 },
    { t: 0.4, chest: deg(-72), hips: deg(-45), lean: deg(3), rise: -0.6, bow: "aim", hand: "anchor", draw: 1, feet: 1 },
    { t: 0.5, chest: deg(-72), hips: deg(-45), lean: deg(3), rise: -0.6, bow: "aim", hand: "anchor", draw: 1, feet: 1 },
    { t: 0.56, chest: deg(-74), hips: deg(-45), lean: deg(3), rise: -0.6, bow: "aim", hand: "loose", draw: 0, feet: 1 },
    { t: 0.74, chest: deg(-70), hips: deg(-45), lean: deg(2), rise: -0.5, bow: "follow", hand: "loose", draw: 0, feet: 1 },
    { t: 1, chest: 0, hips: 0, lean: 0, rise: 0, bow: "rest", hand: "rest", draw: 0, feet: 0 },
]

const lerp3 = (a: Vec3, b: Vec3, u: number): Vec3 => [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u]
const unit = (v: Vec3): Vec3 => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l] }

/** Lower the pelvis until every planted foot is in reach from where the hips now are. */
const grounded = (pose: PoseSample, p: Proportions): PoseSample => {
    const sk = solvePose(pose, p)
    let height = pose.root[1]
    for (const side of ["L", "R"] as const) {
        const leg = pose.legs[side]
        if (leg.mode === "ik" && leg.planted) height = Math.min(height, maxPelvisHeight(leg.ankle, [sk[side].hip[0], sk[side].hip[2]], p))
    }
    return { ...pose, root: [pose.root[0], height, pose.root[2]] }
}

const sampleBowShot = (stance: number) => (t: number, p: Proportions, facing?: string): PoseSample => {
    const rest = restingSample(p, facing)
    const still = solvePose(rest, p)
    const arm = p.upperArm + p.forearm
    const shoulderL: Vec3 = [p.shoulderWidth, p.shoulderHeight, 0]
    const shoulderR: Vec3 = [-p.shoulderWidth, p.shoulderHeight, 0]
    // Where a hand is, from its shoulder, in the chest's frame -- for a chest turned `chest` from the feet.
    const place = (k: ShotKey) => {
        const target: Vec3 = [-Math.sin(k.chest), 0, Math.cos(k.chest)]
        const rightOfFace: Vec3 = [-target[2], 0, target[0]]
        const chin = p.neck + p.headCenter - p.head[1] * 0.75
        const anchor = add(add([0, chin, 0], scale(target, 1.0)), scale(rightOfFace, 1.4))
        const level = Math.min(0.9, (chin - p.shoulderHeight) / (arm * 0.95))
        const aimDir = unit(add(scale(target, Math.sqrt(1 - level * level)), [0, level, 0]))
        const bowHand: Record<BowAt, Vec3> = {
            rest: sub(still.L.hand, add(still.chest, shoulderL)),
            raise: scale(unit(add(scale(target, 0.85), [0, -0.25, 0])), arm * 0.75),
            aim: scale(aimDir, arm * 0.95),
            follow: scale(unit(add(aimDir, [0, -0.12, 0])), arm * 0.94),
        }
        const bowAt = add(shoulderL, bowHand[k.bow])
        const drawHand: Record<HandAt, Vec3> = {
            rest: sub(still.R.hand, add(still.chest, shoulderR)),
            nock: sub(add(bowAt, scale(target, -3)), shoulderR),
            anchor: sub(anchor, shoulderR),
            loose: sub(add(add(anchor, scale(target, -3)), scale(rightOfFace, 0.6)), shoulderR),
        }
        const restPole = (side: "L" | "R"): Vec3 =>
            unit(sub(still[side].elbow, scale(add(still[side].shoulder, still[side].hand), 0.5)))
        return {
            bow: bowHand[k.bow],
            hand: drawHand[k.hand],
            bowPole: k.bow === "rest" ? restPole("L") : unit(add(scale(target, -0.2), [0, -1, 0])),
            handPole: k.hand === "rest" ? restPole("R") : unit(add(add(scale(target, -1), [0, 0.6, 0]), scale(rightOfFace, 0.5))),
            grip: k.bow === "rest" ? still.L.grip : unit(sub([0, 1, 0], scale(rightOfFace, 0.15))),
        }
    }
    let i = 0
    while (i < SHOT_KEYS.length - 2 && t > SHOT_KEYS[i + 1].t) i++
    const a = SHOT_KEYS[i], b = SHOT_KEYS[i + 1]
    const u = ease(b.t > a.t ? (t - a.t) / (b.t - a.t) : 0)
    const A = place(a), B = place(b)
    const chest = mix(a.chest, b.chest, u), hips = mix(a.hips, b.hips, u)
    // Chest at `chest`, pelvis at `hips` (see sampleKeys).
    const twist = (chest - hips) / (1 + PELVIS_COUNTER_TWIST)
    const yaw = hips + PELVIS_COUNTER_TWIST * twist
    const feet = mix(a.feet, b.feet, u)
    const foot = (side: Side, z: number): LegTarget => {
        const r = rest.legs[side] as LegTarget
        return { ...r, ankle: [r.ankle[0], r.ankle[1], r.ankle[2] * (1 - feet) + z * feet], planted: true }
    }
    return grounded(
        {
            ...rest,
            root: [rest.root[0], rest.root[1] + mix(a.rise, b.rise, u), rest.root[2]],
            rootRotation: rotY(yaw),
            twist,
            lean: mix(a.lean, b.lean, u),
            // Eyes on the target throughout.
            headYaw: -yaw - 0.4 * twist,
            headPitch: 0,
            arms: {
                L: { ...rest.arms.L, reach: lerp3(A.bow, B.bow, u), pole: unit(lerp3(A.bowPole, B.bowPole, u)), grip: unit(lerp3(A.grip, B.grip, u)) },
                R: { ...rest.arms.R, reach: lerp3(A.hand, B.hand, u), pole: unit(lerp3(A.handPole, B.handPole, u)) },
            },
            legs: { L: foot("L", stance * legLength(p)), R: foot("R", -stance * legLength(p)) },
            draw: mix(a.draw, b.draw, u),
        },
        p,
    )
}

/** A real body's ready stance: feet staggered under the hips, hands up a little. */
const READY_NATURAL: Key = {
    t: 0,
    twist: 0,
    hips: 0,
    head: 0,
    armL: [deg(10), deg(14), deg(25)],
    armR: [deg(20), deg(12), deg(45)],
    feetL: [0.8, 5.5],
    feetR: [0.8, -4.5],
}

/**
 * The same right-handed overhead-to-across strike for a body at real proportions: the feet stay
 * put, the hips turn only a little and WITH the blow, and the chest and arm do the work -- the
 * shared slash's pelvis counter-swing reads as the hips swaying on a long-legged body.
 */
const NATURAL_SLASH_KEYS: Key[] = [
    READY_NATURAL,
    {
        ...READY_NATURAL,
        t: 0.3,
        rise: -0.5,
        lean: deg(-2),
        twist: deg(-26),
        hips: deg(-8),
        head: deg(-6),
        armL: [deg(25), deg(18), deg(35)],
        armR: [deg(150), deg(28), deg(70), deg(90)],
    },
    {
        ...READY_NATURAL,
        t: 0.55,
        rise: -1.3,
        lean: deg(8),
        twist: deg(26),
        hips: deg(8),
        head: deg(6),
        armL: [deg(-18), deg(20), deg(30)],
        armR: [deg(75), deg(-25), deg(8), deg(20)],
    },
    {
        ...READY_NATURAL,
        t: 0.75,
        rise: -1.1,
        lean: deg(10),
        twist: deg(30),
        hips: deg(9),
        head: deg(6),
        armL: [deg(-14), deg(18), deg(30)],
        armR: [deg(15), deg(-40), deg(15), deg(30)],
    },
    { ...READY_NATURAL, t: 1 },
]

/**
 * A spell cast: the hands brought together before the chest, then raised high and held there --
 * LPC's spellcast row, which the game holds on its last frame while the spell lasts.
 */
const castKeys = (ready: Key): Key[] => {
    const before: Key = { ...ready, armL: [deg(70), deg(-8), deg(45)], armR: [deg(70), deg(-8), deg(45)] }
    const raised: Key = { ...ready, armL: [deg(150), deg(12), deg(15)], armR: [deg(150), deg(12), deg(15)], lean: deg(-4) }
    return [ready, { ...before, t: 0.35 }, { ...raised, t: 0.75 }, { ...raised, t: 1 }]
}

/**
 * A polearm carried: whatever the clip does with the right arm, the hand stays where it puts it,
 * closed round the shaft, and the pole stands upright in it -- leaning a little ahead, the butt
 * toward the ground -- as a guard carries a spear, instead of jutting forward along the wrist.
 */
export const poleCarry = (clip: EmberClip): EmberClip => ({
    ...clip,
    sample: (t, p, facing) => {
        const pose = clip.sample(t, p, facing)
        const sk = solvePose(pose, p)
        const toChest = transpose(sk.torsoBasis)
        const R = sk.R
        const bend = unit(sub(R.elbow, scale(add(R.shoulder, R.hand), 0.5)))
        const arm: ArmPose = {
            ...pose.arms.R,
            reach: apply(toChest, sub(R.hand, R.shoulder)),
            pole: dot(bend, bend) > 0.5 ? apply(toChest, bend) : [-0.3, 0, -1],
            grip: [0, 1, 0.12],
        }
        return { ...pose, arms: { ...pose.arms, R: arm } }
    },
})

/**
 * Standing still: the right hand's weapon rests as its design holds it (`hold`, degrees: a sword's
 * tip let down low ahead, a dagger tipped out from the thigh) instead of carried shaft-up, and the
 * empty hands (`open`) hang open, fingers down. A hand placed by its reach keeps what it holds.
 */
export const atRest = (clip: EmberClip, hold: { wrist?: number; out?: number } | undefined, open: Side[]): EmberClip => ({
    ...clip,
    sample: (t, p, facing) => {
        const pose = clip.sample(t, p, facing)
        const arm = (side: Side): ArmPose => {
            const a = pose.arms[side]
            if (a.reach) return a
            const held = side === "R" && hold
            return {
                ...a,
                ...(held && hold.wrist !== undefined ? { wrist: deg(hold.wrist) } : {}),
                ...(held && hold.out !== undefined ? { out: deg(hold.out) } : {}),
                ...(open.includes(side) ? { open: true } : {}),
            }
        }
        return { ...pose, arms: { L: arm("L"), R: arm("R") } }
    },
})

/** The clips a body carrying a load in its arms plays with them full (poleCarry's, for a load). */
export const CARRY_CLIPS = ["idle", "walk", "run", "jump"]

/**
 * A load carried in both arms -- an armful of wood, a sack, a stone -- as LPC carries one: the hands
 * brought together in front of the belly, forearms forward under it, elbows out, leaning back a
 * little against the weight, over whatever the legs are doing. The load itself is the game's item
 * art, drawn between the hands (the client, from the frame's joints).
 */
export const carrying = (clip: EmberClip): EmberClip => ({
    ...clip,
    sample: (t, p, facing) => {
        const base = clip.sample(t, p, facing)
        let pose: PoseSample = { ...base, lean: base.lean - deg(3) }
        const sk = solvePose(pose, p)
        const hand = (side: Side): Vec3 =>
            add(sk.chest, apply(sk.torsoBasis, [sideSign(side) * p.shoulderWidth * 0.5, -p.spine * 0.6, p.torso[2] + p.handRadius + 1.5]))
        pose = placeHand(pose, p, "L", hand("L"), [1, -0.8, -0.5])
        return placeHand(pose, p, "R", hand("R"), [-1, -0.8, -0.5])
    },
})

/**
 * Where a thrust puts the rear (right) fist, in the chest's frame from the chest's middle, and which
 * way the shaft points from it; the body's lean and turn into it.
 */
interface ThrustKey { t: number; fist: (p: Proportions) => Vec3; aim: Vec3; lean: number; twist: number }

/**
 * A two-handed thrust (LPC's `thrust` row, the spear's and the staff's): the shaft held low across
 * the body in both hands, the right behind, drawn back, driven forward on a lean and a turn of the
 * shoulders, and recovered -- the feet staggered, the left ahead. The front hand rides the shaft
 * `span` ahead of the rear one.
 */
const sampleThrust = (keys: ThrustKey[], span: (p: Proportions) => number, frontHand: (p: Proportions) => Vec3 = () => [0, 0, 0]): Sampler =>
    (t, p, facing) => {
        let i = 0
        while (i < keys.length - 2 && t > keys[i + 1].t) i++
        const a = keys[i], b = keys[i + 1]
        const u = ease(b.t > a.t ? (t - a.t) / (b.t - a.t) : 0)
        const rest = restingSample(p, facing)
        const stagger = legLength(p) * 0.12
        const foot = (side: Side, z: number): LegTarget => {
            const r = rest.legs[side] as LegTarget
            return { ...r, ankle: [r.ankle[0], r.ankle[1], z], planted: true }
        }
        let pose: PoseSample = grounded(
            {
                ...rest,
                lean: mix(a.lean, b.lean, u),
                twist: mix(a.twist, b.twist, u),
                legs: { L: foot("L", stagger), R: foot("R", -stagger) },
            },
            p,
        )
        const sk = solvePose(pose, p)
        const aim = unit(lerp3(a.aim, b.aim, u))
        const fist = add(sk.chest, apply(sk.torsoBasis, lerp3(a.fist(p), b.fist(p), u)))
        const shaft = apply(sk.torsoBasis, aim)
        pose = { ...pose, arms: { ...pose.arms, R: { ...pose.arms.R, grip: aim } } }
        pose = placeHand(pose, p, "R", fist, [-0.6, -1, -0.3])
        const front = add(add(fist, scale(shaft, span(p))), apply(sk.torsoBasis, frontHand(p)))
        return placeHand(pose, p, "L", front, [0.6, -1, -0.2])
    }

const SPEAR_THRUST: ThrustKey[] = [
    { t: 0, fist: p => [-p.shoulderWidth * 0.7, -p.spine * 0.55, p.torso[2] * 0.6], aim: [0.3, 0.3, 1], lean: deg(2), twist: deg(-8) },
    { t: 0.35, fist: p => [-p.shoulderWidth * 0.85, -p.spine * 0.45, -p.torso[2] * 0.3], aim: [0.25, 0.15, 1], lean: deg(-2), twist: deg(-18) },
    // (Aimed up a little: the lean and a side facing's turn toward the camera both tip it down on screen.)
    { t: 0.6, fist: p => [-p.shoulderWidth * 0.35, -p.spine * 0.3, p.torso[2] + (p.upperArm + p.forearm) * 0.3], aim: [0.12, 0.3, 1], lean: deg(10), twist: deg(16) },
    { t: 1, fist: p => [-p.shoulderWidth * 0.7, -p.spine * 0.55, p.torso[2] * 0.6], aim: [0.3, 0.3, 1], lean: deg(2), twist: deg(-8) },
]

/**
 * The crossbow's `thrust` (LPC fires it on that row): raised from the hip to the shoulder, the stock
 * along the line of sight, loosed just past halfway with a kick up, and lowered. The left hand
 * cradles the stock under its front.
 */
const CROSSBOW_SHOT: ThrustKey[] = [
    { t: 0, fist: p => [-p.shoulderWidth * 0.6, -p.spine * 0.5, p.torso[2] * 0.7], aim: [0.1, -0.35, 1], lean: 0, twist: deg(-6) },
    { t: 0.35, fist: p => [-p.shoulderWidth * 0.5, p.shoulderHeight + 0.5, p.torso[2] + 1.5], aim: [0.05, 0.02, 1], lean: deg(-2), twist: deg(-10) },
    { t: 0.55, fist: p => [-p.shoulderWidth * 0.5, p.shoulderHeight + 0.5, p.torso[2] + 1.5], aim: [0.05, 0.02, 1], lean: deg(-2), twist: deg(-10) },
    { t: 0.7, fist: p => [-p.shoulderWidth * 0.5, p.shoulderHeight + 1.3, p.torso[2] + 0.6], aim: [0.05, 0.18, 1], lean: deg(-4), twist: deg(-10) },
    { t: 1, fist: p => [-p.shoulderWidth * 0.6, -p.spine * 0.5, p.torso[2] * 0.7], aim: [0.1, -0.35, 1], lean: 0, twist: deg(-6) },
]
const crossbowCradle = (p: Proportions): Vec3 => [0, -p.handRadius * 1.1, 0]

/** Thrusts and two-handed swings for the game's other weapons, by `<action>:<held>` (planEmberAnimation). */
const weaponClips = (slash: Key[]): Record<string, EmberClip> => {
    const twoHandedSlash = (held: string): EmberClip =>
        ({ name: "slash", frames: 10, frameMs: 40, loop: false, sample: twoHanded(sampleKeys(slash), poleSpan) })
    const thrust: EmberClip = { name: "thrust", frames: 10, frameMs: 40, loop: false, sample: sampleThrust(SPEAR_THRUST, poleSpan) }
    return {
        "slash:waraxe": twoHandedSlash("waraxe"),
        "slash:halberd": twoHandedSlash("halberd"),
        "slash:scythe": twoHandedSlash("scythe"),
        "thrust:spear": thrust,
        "thrust:staff": thrust,
        "thrust:crossbow": {
            name: "thrust",
            frames: 10,
            frameMs: 40,
            loop: false,
            sample: sampleThrust(CROSSBOW_SHOT, p => legLength(p) * 0.3, crossbowCradle),
        },
    }
}

/** Walk, run and strike for bodies at real proportions (styles.ts gives them to the realist style). */
export const NATURAL_CLIPS: Record<string, EmberClip> = {
    walk: {
        name: "walk",
        frames: 16,
        frameMs: 60,
        loop: true,
        strideFor: p => NATURAL_WALK.cycle * legLength(p),
        sample: sampleStride(NATURAL_WALK),
    },
    run: {
        name: "run",
        frames: 12,
        frameMs: 50,
        loop: true,
        strideFor: p => NATURAL_RUN.cycle * legLength(p),
        sample: sampleStride(NATURAL_RUN),
    },
    slash: { name: "slash", frames: 10, frameMs: 40, loop: false, sample: sampleKeys(NATURAL_SLASH_KEYS) },
    "slash:dagger": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: sampleKeys(daggerSlash(READY_NATURAL)) },
    "shoot:bow": { name: "shoot", frames: 14, frameMs: 60, loop: false, sample: sampleBowShot(0.16) },
    cast: { name: "cast", frames: 7, frameMs: 90, loop: false, sample: sampleKeys(castKeys(READY_NATURAL)) },
    "slash:longsword": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: twoHanded(swordCut(longswordCut(READY_NATURAL), LONGSWORD_TO_CAMERA)) },
    "slash:saber": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: handOnHip(swordCut(saberCut(READY_NATURAL), SABER_TO_CAMERA)) },
    // A rapier cuts as the saber does (the game slashes with it), one-handed, the free hand on the hip.
    "slash:rapier": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: handOnHip(swordCut(saberCut(READY_NATURAL), SABER_TO_CAMERA)) },
    ...weaponClips(NATURAL_SLASH_KEYS),
}

/**
 * A zombie's shamble: hunched forward from the hips with the head hanging, and -- when its hands
 * are its own (`reaching`) -- both arms held out ahead at shoulder height, swaying a little out of
 * step with each other. Its legs walk, run or strike as the clip has them; an attack keeps its arms.
 */
export const shambling = (clip: EmberClip, reaching: boolean): EmberClip => ({
    ...clip,
    sample: (t, p, facing) => {
        const pose = clip.sample(t, p, facing)
        const hunched = { ...pose, lean: pose.lean + deg(14), headPitch: pose.headPitch + deg(12), sideBend: pose.sideBend + deg(3) }
        if (!reaching) return hunched
        const sway = Math.sin(t * Math.PI * 2)
        // Level with the shoulders in the world: the hunch tips the chest -- and arms -- forward and down.
        const reach = (phase: number): ArmPose => ({ pitch: deg(100 + 6 * phase), abduct: deg(10), flex: deg(10 - 4 * phase) })
        return { ...hunched, arms: { L: reach(sway), R: reach(-sway) } }
    },
})

/**
 * A shield on the left arm: the forearm held out ahead and level, so the shield gripped in the fist
 * (body.ts shieldOf) stands in front of the body -- carried at the hip, swinging a little, at rest
 * and on the move; raised to cover the chest while attacking. Overrides whatever the clip did with
 * the left arm (a two-handed grip, a hand on the hip): that hand is busy.
 */
export const bearingShield = (clip: EmberClip, raised: boolean): EmberClip => ({
    ...clip,
    sample: (t, p, facing) => {
        const pose = clip.sample(t, p, facing)
        const own = pose.arms.L
        const swing = raised || own.reach ? 0 : (own.pitch - 0.05) * 0.3
        const L: ArmPose = raised
            ? { pitch: deg(42), abduct: deg(12), flex: deg(78) }
            : { pitch: deg(8) + swing, abduct: deg(14), flex: deg(84) }
        return { ...pose, arms: { ...pose.arms, L } }
    },
})

/**
 * An attack thrown on the move: the upper body -- arms, chest, head, the hips' turn into the blow --
 * from the attack, the legs and the pelvis's travel from a gait that carries on from `phase` through
 * `cycles` of its stride while the attack plays. The feet keep walking (or running) under the blow
 * instead of planting into a stance and sliding across the ground.
 */
export const layerClip = (upper: EmberClip, lower: EmberClip, phase: number, cycles: number): EmberClip => ({
    name: `${upper.name}.${lower.name}`,
    frames: upper.frames,
    frameMs: upper.frameMs,
    loop: false,
    sample: (t, p, facing) => {
        const top = upper.sample(t, p, facing)
        const gait = lower.sample((((phase + t * cycles) % 1) + 1) % 1, p, facing)
        const pose: PoseSample = {
            ...gait,
            rootRotation: top.rootRotation,
            lean: gait.lean + top.lean,
            twist: top.twist,
            headPitch: top.headPitch,
            headYaw: top.headYaw,
            arms: top.arms,
            draw: top.draw,
        }
        // The attack turns the hips: a standing foot must still be reached from where they now are.
        const sk = solvePose(pose, p)
        let height = pose.root[1]
        for (const side of ["L", "R"] as const) {
            const leg = pose.legs[side]
            if (leg.mode === "ik" && leg.planted) height = Math.min(height, maxPelvisHeight(leg.ankle, [sk[side].hip[0], sk[side].hip[2]], p))
        }
        return { ...pose, root: [pose.root[0], height, pose.root[2]] }
    },
})

/** The rungs of the ladder (the stairs-up art) are this far apart, in world pixels. */
export const LADDER_RUNG = 8
/** How high one cycle of the climb carries the body: a rung for each of its two steps. */
export const CLIMB_RISE = 2 * LADDER_RUNG

/**
 * Up a ladder, facing it: the left hand and right foot go up two rungs together, then the right hand
 * and left foot, the body rising a rung a step. The body's height is the sprite's to move -- the
 * client raises it as the stair travel climbs, a cycle per CLIMB_RISE -- so a hand or foot holding
 * a rung slides down the body as it rises, staying put on the ladder, and the moving one overtakes
 * it. Played backwards, it climbs down.
 */
const sampleClimb: Sampler = (t, p) => {
    // How far above its lowest a limb is, in the body's frame, for one whose move starts at `start`:
    // carried up two rungs ahead of the body in half a cycle, then held while the body catches up.
    const lift = (start: number) => {
        const u = (((t - start) % 1) + 1) % 1
        return u < 0.5 ? CLIMB_RISE * (smoothstep(u * 2) - u) : CLIMB_RISE * (1 - u)
    }
    const rest = restingSample(p, "back")
    const foot = (side: Side, start: number): LegTarget => ({
        mode: "ik",
        ankle: [sideSign(side) * (p.hipWidth + 0.4), p.ankleHeight + lift(start), p.footLength * 0.45],
        footPitch: 0.15,
    })
    const leaning: PoseSample = {
        ...rest,
        root: [0, standingPelvisHeight(p) - 1.4, 0],
        lean: deg(6),
        headPitch: deg(-12),
        legs: { L: foot("L", 0.5), R: foot("R", 0) },
    }
    // Each hand on a rung just inside the rails, ahead of the chest: the higher one reaching over the
    // head, the lower by the ear.
    const sk = solvePose(leaning, p)
    const grip = (side: Side, start: number): Vec3 => [
        sideSign(side) * (p.shoulderWidth + 1.2),
        sk.head[1] + p.head[1] + 3 - LADDER_RUNG + lift(start),
        p.torso[2] + 2.5,
    ]
    const elbowOut = (side: Side): Vec3 => [sideSign(side), -0.6, -0.4]
    const left = placeHand(leaning, p, "L", grip("L", 0), elbowOut("L"))
    return placeHand(left, p, "R", grip("R", 0.5), elbowOut("R"))
}

/** Turn `a` toward `b` (both unit) by the share `u` of the angle between them. */
const slerp = (a: Vec3, b: Vec3, u: number): Vec3 => {
    const angle = Math.acos(Math.max(-1, Math.min(1, dot(a, b))))
    if (angle < 1e-4) return b
    const s = Math.sin(angle)
    return unit(add(scale(a, Math.sin((1 - u) * angle) / s), scale(b, Math.sin(u * angle) / s)))
}

/**
 * A player's jump (the J key; game-common/jump.ts times it): a quick dip with the arms swung back,
 * a spring off the toes with the arms thrown forward and out, the knees tucked at the top, the legs
 * reaching down for the ground, and a landing taken in the knees. The height is not the clip's --
 * the client lifts the whole frame on the jump's curve -- so the soles leave the frame's ground line
 * only as the legs tuck. Its keys line up with jump.ts: the soles leave the ground just before the
 * spring (JUMP_TAKEOFF), are back on it at the landing (JUMP_LANDING), and a leap off an edge holds
 * the legs-reaching-down key (JUMP_HOLD) all the way down.
 */
const JUMP_KEYS: Key[] = [
    { t: 0, rest: true, armL: [0, 0, 0], armR: [0, 0, 0] },
    // The dip: knees bent, the chest over them, the arms swung back.
    { t: 0.09, rise: -4, lean: deg(16), headPitch: deg(-4),
      armL: [deg(-40), deg(14), deg(25)], armR: [deg(-40), deg(14), deg(25)], feetL: [0.5, 1.2], feetR: [0.5, -1.2] },
    // Off the toes, the arms thrown forward and out.
    { t: 0.18, rise: 1, lean: deg(6), headPitch: deg(-6),
      armL: [deg(72), deg(55), deg(40)], armR: [deg(56), deg(55), deg(40)],
      legL: [deg(6), deg(3), deg(6), 0, deg(35)], legR: [deg(-8), deg(3), deg(10), 0, deg(40)] },
    // The top: knees tucked up, the arms out for balance.
    { t: 0.45, rise: 1, lean: deg(8), headPitch: deg(-4),
      armL: [deg(66), deg(66), deg(45)], armR: [deg(54), deg(66), deg(45)],
      legL: [deg(60), deg(6), deg(85), 0, deg(25)], legR: [deg(35), deg(6), deg(75), 0, deg(30)] },
    // Coming down: the legs reaching for the ground.
    { t: 0.73, lean: deg(6), headPitch: deg(2),
      armL: [deg(46), deg(66), deg(28)], armR: [deg(40), deg(66), deg(28)],
      legL: [deg(22), deg(5), deg(22), 0, deg(12)], legR: [deg(6), deg(5), deg(28), 0, deg(18)] },
    // Down, and taken in the knees.
    { t: 0.86, rise: -2, lean: deg(10),
      armL: [deg(32), deg(50), deg(25)], armR: [deg(32), deg(50), deg(25)], feetL: [0.5, 1.2], feetR: [0.5, -1.2] },
    { t: 0.93, rise: -4, lean: deg(16), headPitch: deg(4),
      armL: [deg(22), deg(34), deg(28)], armR: [deg(22), deg(34), deg(28)], feetL: [0.5, 1.2], feetR: [0.5, -1.2] },
    { t: 1, rest: true, armL: [0, 0, 0], armR: [0, 0, 0] },
]

/** Where a falling body's hands go: from the shoulder, in the chest's frame (+x the body's left), and the elbow's give. */
const FALLING_HAND: Vec3 = [0.5, 0.86, 0.12]
const FALLING_POLE: Vec3 = [0.9, -0.1, -0.6]
const FALLING_REACH = 0.985

/**
 * In the air -- knocked off a ladder, stepped off a roof, dropped through a floor: the arms fly up
 * over the head and out, the elbows giving a little, the legs hanging loose with the toes down and
 * the head tipped back, and held there until the body lands. It sets out from the body's own
 * standing pose in its facing, so played backwards -- the recovery -- the hands come back down to
 * exactly where idle has them.
 */
const sampleFalling: Sampler = (t, p, facing) => {
    const rest = restingSample(p, facing)
    if (t <= 0) return rest
    const u = ease(t)
    const pose: PoseSample = { ...rest, lean: deg(-4) * u, headPitch: deg(-10) * u }
    const still = solvePose(rest, p)
    const toChest = transpose(still.torsoBasis)
    const arm = p.upperArm + p.forearm
    const raise = (side: Side): ArmPose => {
        const s = sideSign(side)
        const from = apply(toChest, sub(still[side].hand, still[side].shoulder))
        const length = Math.hypot(from[0], from[1], from[2])
        const to = unit([s * FALLING_HAND[0], FALLING_HAND[1], FALLING_HAND[2]])
        const bend = unit(sub(still[side].elbow, scale(add(still[side].shoulder, still[side].hand), 0.5)))
        const restPole = apply(toChest, bend)
        const pole = unit(lerp3(restPole, [s * FALLING_POLE[0], FALLING_POLE[1], FALLING_POLE[2]], u))
        return { ...rest.arms[side], reach: scale(slerp(unit(from), to, u), length + (arm * FALLING_REACH - length) * u), pole }
    }
    // The legs dangle: each ankle drawn up a little under its knee, the left more, toes pointing down.
    const dangle = (side: Side, up: number, back: number, toes: number): LegTarget => {
        const r = rest.legs[side] as LegTarget
        return { ...r, ankle: [r.ankle[0], r.ankle[1] + up * u, r.ankle[2] - back * u], footPitch: toes * u, planted: false }
    }
    return {
        ...pose,
        arms: { L: raise("L"), R: raise("R") },
        legs: { L: dangle("L", 4, 1.5, 0.55), R: dangle("R", 3, -0.5, 0.4) },
    }
}

export const EMBER_CLIPS: Record<string, EmberClip> = {
    idle: { name: "idle", frames: 1, frameMs: 1000, loop: true, sample: (_t, p, facing) => restingSample(p, facing) },
    walk: { name: "walk", frames: 12, frameMs: 70, loop: true, strideCycle: strideOf(WALK), sample: sampleGait(WALK) },
    run: { name: "run", frames: 10, frameMs: 55, loop: true, strideCycle: strideOf(RUN), sample: sampleGait(RUN) },
    slash: { name: "slash", frames: 10, frameMs: 40, loop: false, sample: sampleKeys(SLASH_KEYS) },
    // An attack drawn its own way for one held weapon is keyed `<clip>:<held>` (see planEmberAnimation).
    "slash:dagger": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: sampleKeys(daggerSlash(READY_SLASH)) },
    "shoot:bow": { name: "shoot", frames: 14, frameMs: 60, loop: false, sample: sampleBowShot(0.16) },
    "slash:longsword": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: twoHanded(swordCut(longswordCut(READY_SLASH), LONGSWORD_TO_CAMERA)) },
    "slash:saber": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: handOnHip(swordCut(saberCut(READY_SLASH), SABER_TO_CAMERA)) },
    "slash:rapier": { name: "slash", frames: 10, frameMs: 40, loop: false, sample: handOnHip(swordCut(saberCut(READY_SLASH), SABER_TO_CAMERA)) },
    ...weaponClips(SLASH_KEYS),
    hurt: { name: "hurt", frames: 8, frameMs: 70, loop: false, sample: sampleKeys(FALL_KEYS) },
    cast: { name: "cast", frames: 7, frameMs: 90, loop: false, sample: sampleKeys(castKeys(READY_SLASH)) },
    climb: { name: "climb", frames: 8, frameMs: 70, loop: true, sample: sampleClimb },
    // Held on its last frame while the body is in the air; the client plays it backwards as it lands.
    falling: { name: "falling", frames: 6, frameMs: 35, loop: false, sample: sampleFalling },
    // Played in step with the jump's lift (game-common/jump.ts): 12 x 40ms is its JUMP_MS.
    jump: { name: "jump", frames: 12, frameMs: 40, loop: false, sample: sampleKeys(JUMP_KEYS) },
}

/** The game's attack and pose vocabulary, in terms of the clips that exist. */
export const CLIP_FOR_ACTION: Record<string, string> = {
    idle: "idle",
    walk: "walk",
    run: "run",
    slash: "slash",
    thrust: "slash",
    shoot: "slash",
    cast: "cast",
    hurt: "hurt",
    climb: "climb",
    falling: "falling",
    jump: "jump",
}

/**
 * Attack durations shared by the animation baker and playback. Bow shots use the weapon's firing
 * interval so another shot never arrives while the previous animation is still playing. Other
 * attacks retain their LPC timing: frames / (animationSpeed * 60fps).
 */
export const LPC_ACTION_DURATION_MS: Record<string, number> = {
    slash: (6 / (0.25 * 60)) * 1000,
    thrust: (8 / (0.55 * 60)) * 1000,
    get shoot() { return contentCatalog.source.profiles.weapons.bow.firingLatchMs as number },
}

/** A keyframed clip's keys as this file writes them (angles in radians): what data clips become (animations.ts). */
export type EmberKey = Key
/** Sample keys over a clip: the keyframed clips' sampler, for clips made from data. */
export const sampleKeyframes = (keys: Key[]): Sampler => sampleKeys(keys)

/** The game's own keyframed clips, as worked examples of what keys can say (Sprite Lab, Claude). */
export const KEYFRAME_EXAMPLES: Record<string, { about: string; frames: number; frameMs: number; loop: boolean; keys: Key[] }> = {
    slash: { about: "The overhead-to-across strike, on an LPC-proportioned body: a ready stance with the feet planted, the arm drawn back up, the cut down and across with the chest turning into it, and back.", frames: 10, frameMs: 40, loop: false, keys: SLASH_KEYS },
    "slash-natural": { about: "The same strike for a body at real proportions: the feet stay put, the hips turn a little with the blow, the chest and arm do the work.", frames: 10, frameMs: 40, loop: false, keys: NATURAL_SLASH_KEYS },
    "slash-dagger": { about: "A dagger's cut: drawn back by the right shoulder, then across the body at chest height and recovered.", frames: 10, frameMs: 40, loop: false, keys: daggerSlash(READY_NATURAL) },
    cast: { about: "A spell cast: the hands brought together before the chest, then raised high and held.", frames: 7, frameMs: 90, loop: false, keys: castKeys(READY_NATURAL) },
    jump: { about: "A jump: a dip with the arms swung back, a spring off the toes, the knees tucked at the top, the legs reaching down, and the landing taken in the knees. The height is not in the keys: the game lifts the whole body on the jump's curve.", frames: 12, frameMs: 40, loop: false, keys: JUMP_KEYS },
    hurt: { about: "A fall: the knees buckle, the body folds over them, pitches forward onto its hands and ends face down -- each key after the first let down onto whatever of it is lowest (ground: 1).", frames: 8, frameMs: 70, loop: false, keys: FALL_KEYS },
}
